import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InlineNotice } from "@/components/common/InlineNotice";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/common/StatusChip";
import { MapPin, DollarSign, ShieldCheck, ArrowRight } from "lucide-react";
import { BadgeList } from "@/components/common/Badges";

export default function BallboyHome() {
  const { user, setUser } = useAuth();
  const [available, setAvailable] = useState(!!user?.available);
  const [jobs, setJobs] = useState(null);
  const [earn, setEarn] = useState(null);
  const [verif, setVerif] = useState(null);

  async function load() {
    try {
      const [oj, e, v] = await Promise.all([
        api.get("/ballboy/open-jobs"),
        api.get("/ballboy/earnings"),
        api.get("/ballboy/verification"),
      ]);
      setJobs(oj.data.items || []);
      setEarn(e.data);
      setVerif(v.data);
    } catch {}
  }

  useEffect(() => { load(); }, []);

  async function toggle(v) {
    setAvailable(v);
    try {
      const { data } = await api.patch("/profile", { available: v });
      setUser(data.user);
      toast.success(v ? "You're online" : "You're offline");
    } catch {
      setAvailable(!v);
    }
  }

  async function setLocation() {
    if (!navigator.geolocation) return toast.error("Geolocation unsupported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.patch("/profile", { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setUser(data.user);
        toast.success("Location updated");
        load();
      } catch {}
    });
  }

  async function accept(id) {
    try {
      await api.post(`/bookings/${id}/accept`);
      toast.success("Job accepted!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
      load();
    }
  }

  return (
    <div className="space-y-4">
      {user && !user.profile_completed && (
        <InlineNotice tone="warning" title="Complete your ball-boy profile">
          Tell athletes about your experience, services, availability, and safety info so you can start accepting jobs. <Link to="/profile" className="underline" data-testid="complete-profile-link">Go to profile →</Link>
        </InlineNotice>
      )}
      {verif && verif.verification_status !== "approved" && (
        <InlineNotice tone={verif.verification_status === "rejected" ? "destructive" : "warning"} title="Verification required">
          {verif.verification_status === "pending" && "Your ID is under review. You'll be able to accept jobs once approved."}
          {verif.verification_status === "rejected" && (verif.reason || "Rejected. Please re-upload your ID from Profile.")}
          {verif.verification_status === "none" && (
            <>Please upload your ID on the <Link to="/profile" className="underline">Profile page</Link>.</>
          )}
        </InlineNotice>
      )}

      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Availability</div>
            <div className="text-xl font-semibold bb-display">{available ? "Online" : "Offline"}</div>
            {user?.badges?.length > 0 && (
              <div className="mt-2"><BadgeList badges={user.badges} max={4} /></div>
            )}
          </div>
          <Switch
            checked={available}
            onCheckedChange={toggle}
            disabled={verif?.verification_status !== "approved" || !user?.profile_completed}
            data-testid="ballboy-availability-switch"
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> Earnings</div>
            <div className="text-xl font-semibold bb-display tabular-nums" data-testid="earnings-balance">
              ${earn ? earn.earnings_total.toFixed(2) : "--"}
            </div>
            <div className="text-xs text-muted-foreground">Pending payout: ${earn ? earn.earnings_pending_payout.toFixed(2) : "--"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Status</div>
            <div className="mt-1"><StatusChip status={verif?.verification_status || "none"} /></div>
            <div className="text-xs text-muted-foreground mt-1">Sports: {(user?.sports || []).join(", ") || "—"}</div>
          </CardContent>
        </Card>
      </div>

      <Button variant="secondary" className="w-full" onClick={setLocation} data-testid="ballboy-update-location">
        <MapPin className="h-4 w-4 mr-1" /> Update my location
      </Button>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold bb-display">Open jobs nearby</h3>
          <Link to="/jobs" className="text-xs text-primary">See all <ArrowRight className="inline h-3 w-3" /></Link>
        </div>
        {jobs === null ? (
          <div className="space-y-2"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
        ) : jobs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No open jobs right now. Stay online — new ones arrive in real time.
          </div>
        ) : (
          <div className="space-y-2" data-testid="ballboy-open-jobs-list">
            {jobs.slice(0, 5).map((j) => (
              <div key={j.id} className="rounded-xl border border-border bg-card p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-medium capitalize bb-display">{j.sport}</div>
                    <div className="text-xs text-muted-foreground">{new Date(j.start_at).toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">{j.address || "Pin on map"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">${j.price_total?.toFixed?.(2)}</div>
                    <Button size="sm" className="mt-1" onClick={() => accept(j.id)} data-testid="ballboy-accept-job">
                      Accept
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
